import { useState, useCallback, useEffect } from 'react';
import type { ContractTemplate, ContractPrintSettings } from './types';
import { genId } from './types';
import {
  loadContractTemplatesForTable,
  loadPreferences,
  savePreferences,
  saveContractTemplatesForTable,
} from '../store/persistence';
import { DEFAULT_CONTRACT_SETTINGS, MAX_CONTRACT_TEMPLATES_PER_TABLE } from '../utils/constants';
import { track } from '../utils/track';

export interface UseContractTemplatesResult {
  templates: ContractTemplate[];
  selectedId: string | null;
  setSelected: (id: string | null) => void;
  /** 超过每表上限时返回 null（调用方需给出提示），不再静默丢掉最旧的模板。 */
  create: (name: string) => ContractTemplate | null;
  /** 插入一份已构建好的模板（内置模板库套用用）。超上限返回 null。 */
  insert: (tpl: ContractTemplate) => ContractTemplate | null;
  update: (id: string, patch: Partial<Pick<ContractTemplate, 'name' | 'sections' | 'settings' | 'overlays' | 'visibility' | 'shared' | 'sharedBy'>>) => void;
  remove: (id: string) => void;
  duplicate: (id: string) => ContractTemplate | null;
}

/** Fallback key for sessions where the Lark Base context isn't available yet
 *  (e.g. plugin opened outside the sidebar). Templates created in this bucket
 *  stay local; once the real tableId resolves, the hook reloads under that key. */
const NO_TABLE_KEY = '__no_table__';

/** 到达上限时告诉界面，由 App 统一弹提示 —— hook 里没有 antd 的 message 上下文。 */
function warnLimit(): void {
  try {
    window.dispatchEvent(new CustomEvent('pp-template-limit'));
  } catch {
    /* 非浏览器环境忽略 */
  }
}

function readLastSelected(storageKey: string): string | null {
  return loadPreferences().lastContractTemplateIdByTable?.[storageKey] ?? null;
}

function writeLastSelected(storageKey: string, id: string | null): void {
  const prefs = loadPreferences();
  const map = { ...(prefs.lastContractTemplateIdByTable ?? {}) };
  if (id == null) delete map[storageKey];
  else map[storageKey] = id;
  savePreferences({ ...prefs, lastContractTemplateIdByTable: map });
}

export function useContractTemplates(tableId: string | null): UseContractTemplatesResult {
  const storageKey = tableId ?? NO_TABLE_KEY;
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const load = () => {
      const loaded = loadContractTemplatesForTable(storageKey);
      setTemplates(loaded);
      setSelectedId((prev) => {
        if (prev && loaded.some((t) => t.id === prev)) return prev;
        // 每个页面都是一份独立的 hook 实例；不从偏好里读回来的话，
        // 首页 → 模板库 → 首页 就会跳回「最近编辑的那个」，打印容易打错。
        const remembered = readLastSelected(storageKey);
        if (remembered && loaded.some((t) => t.id === remembered)) return remembered;
        return loaded.length > 0 ? loaded[0].id : null;
      });
    };
    load();
    // 云同步合并完成后重载
    window.addEventListener('pp-templates-reloaded', load);
    return () => window.removeEventListener('pp-templates-reloaded', load);
  }, [storageKey]);

  const selectTemplate = useCallback(
    (id: string | null) => {
      setSelectedId(id);
      writeLastSelected(storageKey, id);
    },
    [storageKey],
  );

  const persist = useCallback(
    (next: ContractTemplate[]) => {
      setTemplates(next);
      saveContractTemplatesForTable(storageKey, next);
    },
    [storageKey],
  );

  const create = useCallback(
    (name: string): ContractTemplate | null => {
      if (templates.length >= MAX_CONTRACT_TEMPLATES_PER_TABLE) {
        warnLimit();
        return null;
      }
      const now = Date.now();
      const tpl: ContractTemplate = {
        id: genId(),
        tableId: storageKey,
        name,
        sections: [],
        settings: { ...DEFAULT_CONTRACT_SETTINGS } as ContractPrintSettings,
        createdAt: now,
        updatedAt: now,
      };
      const next = [tpl, ...templates];
      persist(next);
      selectTemplate(tpl.id);
      return tpl;
    },
    [storageKey, templates, persist, selectTemplate],
  );

  const update = useCallback(
    (id: string, patch: Partial<Pick<ContractTemplate, 'name' | 'sections' | 'settings' | 'overlays' | 'visibility' | 'shared' | 'sharedBy'>>) => {
      // 改过内容 = 真的在搭版，而不是建完就走
      if (patch.sections || patch.settings || patch.overlays) track('tpl_edit');
      const next = templates.map((t) =>
        t.id === id ? { ...t, ...patch, updatedAt: Date.now() } : t,
      );
      persist(next);
    },
    [templates, persist],
  );

  const insert = useCallback(
    (tpl: ContractTemplate): ContractTemplate | null => {
      if (templates.length >= MAX_CONTRACT_TEMPLATES_PER_TABLE) {
        warnLimit();
        return null;
      }
      // 归到当前表键下，避免库模板 tableId 与当前上下文不一致
      const t: ContractTemplate = { ...tpl, tableId: storageKey, updatedAt: Date.now() };
      persist([t, ...templates]);
      selectTemplate(t.id);
      return t;
    },
    [storageKey, templates, persist, selectTemplate],
  );

  const remove = useCallback(
    (id: string) => {
      const next = templates.filter((t) => t.id !== id);
      persist(next);
      if (selectedId === id) {
        selectTemplate(next.length > 0 ? next[0].id : null);
      }
    },
    [templates, selectedId, persist, selectTemplate],
  );

  const duplicate = useCallback(
    (id: string): ContractTemplate | null => {
      const src = templates.find((t) => t.id === id);
      if (!src) return null;
      if (templates.length >= MAX_CONTRACT_TEMPLATES_PER_TABLE) {
        warnLimit();
        return null;
      }
      const now = Date.now();
      const copy: ContractTemplate = {
        ...structuredClone(src),
        id: genId(),
        name: `${src.name} (副本)`,
        createdAt: now,
        updatedAt: now,
      };
      const next = [copy, ...templates];
      persist(next);
      selectTemplate(copy.id);
      return copy;
    },
    [templates, persist, selectTemplate],
  );

  return {
    templates,
    selectedId,
    setSelected: selectTemplate,
    create,
    insert,
    update,
    remove,
    duplicate,
  };
}
